import { FieldConfig } from "../types/formTypes";

export interface CitizenshipFormData {
  // Applicant Info
  empId: number;
  empNuid: string;
  empLastName: string;
  empFirstName: string;
  empMiddleName?: string;
  empWorkPhone?: number;
  homePhone?: number;
  empWorkEmail: string;
  empAddress1: string;
  empAddress2?: string;
  empCity: string;
  empState: string;
  empPostalZip: number;
  altPhone?: number;
  altEmail?: string;

  // Employment Info
  shiftStdHours: number;
  empHireDate: string;
  empRehireDate: string;
  regTemp: string;
  empType: string;
  jobTitleCode: number;
  jobTitleName: string;
  unionName: string;
  fullPartTime: string;
  regularTemporary: string;
  bargUnitDesc: string;
  empShift: string;
  mgrNuid: string;
  mgrName: string;
  mgrEmail: string;
  mgrPhone: string;

  // Eligibility
  eligibility?: {
    isUnionMember: boolean;
    is90DaysTenureMet: boolean;
  };

  // Summary
  filingFee: number;
  n400ApplicationDate: string;
  biometricsFee: number;
  totalClassFees: number;
  classSchoolName: string;
  classStartDate: string;
  classEndDate: string;
  comments: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const NONEDITABLE_HELPER_TEXT =
  "Read only; this value is assigned automatically by HR";

const PHONE_HELPER_TEXT = "Edit field; must have 10 digits";
const EMAIL_HELPER_TEXT = "Edit field; Must include '@' and '.com'.";
const PHONE_PLACEHOLDER = "i.e. 123-456-7890";
const EMAIL_PLACEHOLDER = "i.e. you@example.com";

const NUMBER_HELPER_TEXT = "Edit field; numbers only";

const DATE_HELPER_TEXT = "Edit field; MM/DD/YY.";
const DATE_PLACEHOLDER = "i.e. 10/07/2025";
const DATE_ERROR_TEXT = "Invalid date format.";

// ============================================================================
// FIELD BUILDERS: Reduce duplication in readonly fields
// ============================================================================

/**
 * Creates a readonly field configuration
 */
const createReadonlyField = (
  name: string,
  label: string,
  type: "text" | "number" | "email" = "text"
): FieldConfig => ({
  name,
  label,
  type,
  helperText: NONEDITABLE_HELPER_TEXT,
  disabled: true,
});

/**
 * Creates an editable phone field
 */
const createPhoneField = (name: string, label: string): FieldConfig => ({
  name,
  label,
  type: "text",
  placeholder: PHONE_PLACEHOLDER,
  helperText: PHONE_HELPER_TEXT,
  errorText: "",
  validations: [
    {
      type: "phone",
      message: "Invalid phone number. Please enter a 10-digit phone number.",
    },
  ],
});

/**
 * Creates an editable email field
 */
const createEmailField = (name: string, label: string): FieldConfig => ({
  name,
  label,
  type: "email",
  placeholder: EMAIL_PLACEHOLDER,
  helperText: EMAIL_HELPER_TEXT,
  errorText: "",
  validations: [
    {
      type: "email",
      message: "Invalid email address. Please enter a valid email.",
    },
  ],
});

/**
 * Creates a numeric fee field with "at least one" validation
 */
const createFeeField = (
  name: string,
  label: string,
  placeholder: string,
  relatedFields: string[]
): FieldConfig => ({
  name,
  label,
  type: "number",
  helperText: NUMBER_HELPER_TEXT,
  placeholder,
  errorText: "Please enter a valid amount.",
  validations: [
    {
      type: "atLeastOne",
      relatedFields,
      message: "User Input is required. At least one fee must be provided.",
    },
    {
      type: "min",
      value: 1,
      message: "Enter a valid numeric amount.",
    },
  ],
});

/**
 * Creates a date field with format and requiredIf validation
 */
const createDateField = (
  name: string,
  label: string,
  conditionFn?: (formData: Record<string, any>) => boolean,
  conditionMessage?: string,
  additionalValidations?: FieldConfig["validations"]
): FieldConfig => {
  const validations: FieldConfig["validations"] = [
    {
      type: "dateFormat",
      message: "Invalid date format. Use MM/DD/YYYY",
    },
  ];

  if (conditionFn && conditionMessage) {
    validations.push({
      type: "requiredIf",
      condition: conditionFn,
      message: conditionMessage,
    });
  }

  if (additionalValidations) {
    validations.push(...additionalValidations);
  }

  return {
    name,
    label,
    type: "date",
    helperText: DATE_HELPER_TEXT,
    errorText: DATE_ERROR_TEXT,
    placeholder: DATE_PLACEHOLDER,
    validations,
  };
};

// ============================================================================
// CONDITION FUNCTIONS: Extract condition logic
// ============================================================================

/**
 * Check if a fee value exists
 */
const hasFeeValue = (formData: Record<string, any>, feeField: string): boolean => {
  const fees = formData?.[feeField];
  return fees !== undefined && fees !== "" && fees !== null;
};

/**
 * Check if filing fee exists
 */
const hasFilingFee = (formData: Record<string, any>): boolean =>
  hasFeeValue(formData, "filingFee");

/**
 * Check if total class fees exist
 */
const hasTotalClassFees = (formData: Record<string, any>): boolean =>
  hasFeeValue(formData, "totalClassFees");

/**
 * Check if any class detail exists
 */
const hasAnyClassDetail = (formData: Record<string, any>): boolean => {
  const startDate = formData?.classStartDate;
  const endDate = formData?.classEndDate;
  const schoolName = formData?.classSchoolName;

  const hasStartDate = startDate !== undefined && startDate !== "" && startDate !== null;
  const hasEndDate = endDate !== undefined && endDate !== "" && endDate !== null;
  const hasSchoolName = schoolName !== undefined && schoolName !== "" && schoolName !== null;

  return hasStartDate || hasEndDate || hasSchoolName;
};

// ============================================================================
// STEP 1: APPLICANT INFO FIELDS
// ============================================================================

export const applicantFormFields: FieldConfig[] = [
  createReadonlyField("empId", "Employee ID", "number"),
  createReadonlyField("empNuid", "NUID"),
  createReadonlyField("empLastName", "Last name"),
  createReadonlyField("empFirstName", "First name"),
  createReadonlyField("empMiddleName", "MI/Middle name"),
  createReadonlyField("empHomePhone", "Home phone"),
  createReadonlyField("empWorkPhone", "Work phone"),
  createReadonlyField("empWorkEmail", "Work email address", "email"),
  createReadonlyField("empAddress1", "Address 1"),
  createReadonlyField("empAddress2", "Address 2"),
  createReadonlyField("empCity", "City"),
  createReadonlyField("empState", "State"),
  createReadonlyField("empPostalZip", "Zip code", "number"),
  createPhoneField("altPhone", "Alternate phone"),
  createEmailField("altEmail", "Home email address"),
];

// ============================================================================
// STEP 2: EMPLOYMENT INFO FIELDS
// ============================================================================

export const employmentInfoFields: FieldConfig[] = [
  createReadonlyField("shiftStdHours", "Standard hours", "number"),
  createReadonlyField("empHireDate", "Hire date"),
  createReadonlyField("empRehireDate", "Rehire date"),
  createReadonlyField("empJobStatus", "Employee status"),
  createReadonlyField("empType", "Employee type"),
  createReadonlyField("jobTitleCode", "Job code"),
  createReadonlyField("jobTitleName", "Job title"),
  createReadonlyField("unionName", "Union name"),
  createReadonlyField("fullPartTime", "Full-time or Part-time"),
  createReadonlyField("regTemp", "Regular-Temporary"),
  createReadonlyField("bargUnitDesc", "Bargaining unit"),
  createReadonlyField("shiftId", "Employee shift"),
  createReadonlyField("mgrNuid", "Manager NUID"),
  createReadonlyField("mgrName", "Manager name"),
  createReadonlyField("mgrEmail", "Manager email", "email"),
  createReadonlyField("mgrPhone", "Manager phone"),
];

// ============================================================================
// STEP 3: SUMMARY FIELDS
// ============================================================================

const feeRelatedFields = ["filingFee", "biometricsFee", "totalClassFees"];

export const summaryFields: FieldConfig[] = [
  createFeeField("filingFee", "N400 filing fees", "i.e. 987", feeRelatedFields),

  createDateField(
    "n400ApplicationDate",
    "N400 application date",
    hasFilingFee,
    "N400 Application Date is required.",
    [
      {
        type: "minDate",
        value: "2023-01-01",
        message: "Application Date should not be prior to 2023.",
      },
    ]
  ),

  createFeeField(
    "biometricsFee",
    "Biometrics fee",
    "i.e. 530",
    feeRelatedFields
  ),

  {
    name: "totalClassFees",
    label: "Total class fees",
    type: "number",
    helperText: NUMBER_HELPER_TEXT,
    placeholder: "i.e. 1200",
    errorText: "Please enter a valid amount.",
    validations: [
      {
        type: "atLeastOne",
        relatedFields: feeRelatedFields,
        message: "User Input is required. At least one fee must be provided.",
      },
      {
        type: "min",
        value: 1,
        message: "Enter a valid numeric amount.",
      },
      {
        type: "requiredIf",
        condition: hasAnyClassDetail,
        message: "Total class fees is required when class details are entered.",
      },
    ],
  },

  {
    name: "classSchoolName",
    label: "Class/School name",
    type: "text",
    helperText: "Edit field;",
    placeholder: "i.e. Elementary Middle High",
    errorText: "Edit field;",
    validations: [
      {
        type: "requiredIf",
        condition: hasTotalClassFees,
        message: "Class name/School name is required.",
      },
    ],
  },

  createDateField(
    "classStartDate",
    "Class start date",
    hasTotalClassFees,
    "Start date is required. Use MM/DD/YYYY."
  ),

  createDateField(
    "classEndDate",
    "Class end date",
    hasTotalClassFees,
    "End date is required. Use MM/DD/YYYY.",
    [
      {
        type: "dateBefore",
        compareField: "classStartDate",
        message: "End Date must be later than start date.",
      },
    ]
  ),

  {
    name: "comments",
    label: "Employee comments",
    type: "textarea",
    maxLength: 500,
    errorText: "This field is required.",
    validations: [
      {
        type: "maxLength",
        value: 500,
        message: "Employee comments must not exceed 500 characters.",
      },
    ],
  },
];

// ============================================================================
// STEP CONFIGURATION
// ============================================================================

export const citizenshipStepTitles = {
  step1: "Citizenship fee applicant information",
  step2: "Employment Information",
  step3: "Fee summary & comments",
};

export const citizenshipStepContents = {
  step1: {
    body: "All fields are optional unless marked as required.",
  },
  step2: {
    body: "",
  },
  step3: {
    body: "All fields are optional unless marked as required.",
  },
};

export const citizenshipStepFields = {
  step1: applicantFormFields,
  step2: employmentInfoFields,
  step3: summaryFields,
};

export default citizenshipStepFields;